import type {
  DiagnosticProcedureDto,
  ReportResponseDto,
  ReportsFindSimilarReportsParams,
} from '@/api/WhisApi/generated';
import type {
  AssetBodyDto,
  CreateDiagnosticProcedureDto,
  ReportBodyDto,
} from '@/api/WhisApi/generated/model';
import {
  AssetBodyDtoType,
  CreateDiagnosticProcedureDtoCategoryOfTest,
  CreateReportDtoAnimalType,
  CreateReportDtoDomesticAnimalAffected,
  CreateReportDtoHumanAffected,
  CreateReportDtoStatus,
  CreateReportDtoSurveillanceType,
  CreateReportDtoWildType,
} from '@/api/WhisApi/generated/model';
import type { UploadedDocument } from '@/types/formField.types';
import {
  type DiagnosticProcedureFormValue,
  ReportFormData,
  FormField,
  FormFieldValue,
  FormLayout,
  FormSection,
  YesNoUnknownOption,
} from './reportCreation.types';
import {
  buildDiagnosticProcedures,
  isNonEmptyString,
  mapKnownCause,
  mapYesNoUnknown,
  resolveSpeciesValue,
  toDateString,
  toEnumValue,
  toNumber,
} from './utils/reportPayloadMapping.utils';
import { normalizeDependencyName } from './utils/conditionalVisibility.utils';
import { prefixFormSections } from './utils/repeatableSections.utils';

type ReportSubmitMode = 'draft' | 'published';

const mapYesNoUnknownFromApi = (
  value: string | undefined,
): YesNoUnknownOption | undefined => {
  if (!isNonEmptyString(value)) {
    return undefined;
  }

  if (value === CreateReportDtoHumanAffected.yes) {
    return YesNoUnknownOption.yes;
  }

  if (value === CreateReportDtoHumanAffected.no) {
    return YesNoUnknownOption.no;
  }

  return value === CreateReportDtoHumanAffected.dontKnow
    ? YesNoUnknownOption.iDontKnow
    : undefined;
};

const mapDiagnosticProcedureToForm = (
  procedure: DiagnosticProcedureDto,
): DiagnosticProcedureFormValue => ({
  sampling: {
    sampleType: procedure.sampleType?.code,
  },
  tests: {
    category: procedure.categoryOfTest,
    subcategory: procedure.subCategory?.code,
    family: procedure.family?.code,
    extraInfo: procedure.extraTestInformation,
  },
  results: {
    positiveCount: procedure.numberOfPositiveTests,
    negativeCount: procedure.numberOfNegativeTests,
    totalCount: procedure.numberOfTests,
  },
});

export const isFieldVisible = (
  field: FormField,
  dependencyValueMap: Record<string, FormFieldValue>,
): boolean => {
  if (!field.conditional) {
    return true;
  }

  const { dependsOn, visibleWhen } = field.conditional;
  const dependencyValue =
    dependencyValueMap[normalizeDependencyName(dependsOn)];

  if (Array.isArray(visibleWhen)) {
    if (typeof dependencyValue !== 'string') {
      return false;
    }

    return visibleWhen.includes(dependencyValue);
  }

  return dependencyValue === visibleWhen;
};

/**
 * Determines whether the provided value should be considered "filled" for validation purposes.
 */
export const isValueFilled = (value: FormFieldValue): boolean => {
  if (typeof value === 'boolean') {
    return true;
  }

  if (typeof value === 'number') {
    return !isNaN(value);
  }

  return value !== undefined && value !== null && value !== '';
};

/**
 * Expands a form layout into a plain list of sections by duplicating repeatable
 * configurations. Each repeated instance reuses the same section definitions
 * but prefixes field IDs with the array path (e.g. diagnosticProcedures.0.*),
 * which keeps validation logic identical to regular sections.
 */
export const flattenFormLayoutSections = (
  layout: FormLayout,
  repeatableCounts: Record<string, number>,
): FormSection[] => {
  const baseSections = layout.sections ?? [];
  const repeatableSections = layout.repeatableSections ?? [];

  const repeatableInstances = repeatableSections.flatMap((repeatable) => {
    const instanceCount = repeatableCounts[repeatable.id] ?? 0;

    if (instanceCount === 0) {
      return [];
    }

    return Array.from({ length: instanceCount }, (_, index) =>
      prefixFormSections(repeatable.sections, `${repeatable.id}.${index}`),
    ).flat();
  });

  return [...baseSections, ...repeatableInstances];
};

export const buildCreateReportDto = (
  formState: ReportFormData,
  mode: ReportSubmitMode,
  options: {
    code?: string;
    assets?: AssetBodyDto[];
    versionNote?: string;
    specifiedLanguage?: string;
    offlineMode?: boolean;
  },
): ReportBodyDto => {
  const values = formState;
  const location = values.location;
  const knownCause = mapKnownCause(values.knowTheCause);
  const includeEtiology = knownCause === true;
  const diagnosticEnabled = values.diagnosticProcedure === 'yes';
  const animalType = toEnumValue(CreateReportDtoAnimalType, values.animalType);
  const wildType = toEnumValue(CreateReportDtoWildType, values.wildtype);
  const surveillanceType = toEnumValue(
    CreateReportDtoSurveillanceType,
    values.surveillanceType,
  );
  const specie = resolveSpeciesValue(values);
  const latitude = toNumber(location?.latitude);
  const longitude = toNumber(location?.longitude);
  const detectionDate = toDateString(values.dateOfDetection);
  const numberOfDeaths = toNumber(values.numberOfDeaths);
  const numberOfSicks = toNumber(values.numberOfSick);
  const populationSize = toNumber(values.populationSize);
  const humanAffected = mapYesNoUnknown(
    CreateReportDtoHumanAffected,
    values.humanAffected,
  );
  const domesticAnimalAffected = mapYesNoUnknown(
    CreateReportDtoDomesticAnimalAffected,
    values.domesticAnimalAffected,
  );
  const diagnosticProcedures = diagnosticEnabled
    ? buildDiagnosticProcedures(
        values.diagnosticProcedures,
        CreateDiagnosticProcedureDtoCategoryOfTest,
        (procedure): CreateDiagnosticProcedureDto => procedure,
      )
    : undefined;
  const versionNote = isNonEmptyString(options.versionNote)
    ? options.versionNote.trim()
    : undefined;

  return {
    ...(isNonEmptyString(options.specifiedLanguage) && {
      specifiedLanguage: options.specifiedLanguage,
    }),
    ...(isNonEmptyString(options.code) && { code: options.code }),
    status:
      mode === 'draft'
        ? CreateReportDtoStatus.draft
        : CreateReportDtoStatus.published,
    ...(animalType && { animalType }),
    ...(specie && { specie }),
    ...(wildType && { wildType }),
    ...(latitude !== undefined && { latitude }),
    ...(longitude !== undefined && { longitude }),
    ...(typeof values.inaccurateLocation === 'boolean' && {
      inaccurateLocation: values.inaccurateLocation,
    }),
    ...(detectionDate && { detectionDate }),
    ...(surveillanceType && { surveillanceType }),
    ...(isNonEmptyString(values.description) && {
      description: values.description,
    }),
    ...(numberOfDeaths !== undefined && { numberOfDeaths }),
    ...(numberOfSicks !== undefined && { numberOfSicks }),
    ...(populationSize !== undefined && { populationSize }),
    ...(humanAffected && { humanAffected }),
    ...(domesticAnimalAffected && { domesticAnimalAffected }),
    ...(knownCause !== undefined && { knownCause }),
    ...(includeEtiology &&
      isNonEmptyString(values.etiologyType) && {
        etiologyTypeCode: values.etiologyType,
      }),
    ...(includeEtiology &&
      isNonEmptyString(values.etiologyAgent) && {
        etiologicalAgentCode: values.etiologyAgent,
      }),
    ...(diagnosticProcedures && { diagnosticProcedures }),
    ...(versionNote && { versionNote }),
    ...(options.assets && { assets: options.assets }),
    ...(options.offlineMode === true && { offlineMode: true }),
  };
};

export const buildSimilarReportsParams = (
  values: ReportFormData,
): ReportsFindSimilarReportsParams | null => {
  const latitude = toNumber(values.location?.latitude);
  const longitude = toNumber(values.location?.longitude);
  const specie = resolveSpeciesValue(values);

  if (latitude === undefined || longitude === undefined || !specie) {
    return null;
  }

  const includeEtiologyAgent = values.knowTheCause === YesNoUnknownOption.yes;
  const etiologicalAgentCode =
    includeEtiologyAgent && isNonEmptyString(values.etiologyAgent)
      ? values.etiologyAgent
      : undefined;

  return {
    latitude,
    longitude,
    specie,
    ...(etiologicalAgentCode && { etiologicalAgentCode }),
  };
};

export const buildEditFormDataFromReport = (
  report: ReportResponseDto,
): Partial<ReportFormData> => {
  const hasDiagnosticProcedures = report.diagnosticProcedures?.length > 0;
  const diagnosticProcedures = hasDiagnosticProcedures
    ? report.diagnosticProcedures.map(mapDiagnosticProcedureToForm)
    : undefined;

  let knownCause: YesNoUnknownOption | undefined;
  if (report.knownCause !== undefined) {
    knownCause = report.knownCause
      ? YesNoUnknownOption.yes
      : YesNoUnknownOption.no;
  }

  const supportingDocuments: UploadedDocument[] | undefined = report.assets
    ?.filter(
      (asset) =>
        !asset.type || asset.type === AssetBodyDtoType.supportingDocument,
    )
    ?.map((asset) => ({
      id: asset.id,
      uri: asset.fileName,
      name: asset.name || asset.fileName,
      fileName: asset.fileName,
    }))
    .filter((asset) => Boolean(asset.fileName));

  return {
    animalType: toEnumValue(CreateReportDtoAnimalType, report.animalType),
    species: report.specie,
    wildtype: toEnumValue(CreateReportDtoWildType, report.wildType),
    location: {
      latitude:
        report.latitude !== undefined ? String(report.latitude) : undefined,
      longitude:
        report.longitude !== undefined ? String(report.longitude) : undefined,
    },
    inaccurateLocation: report.inaccurateLocation,
    dateOfDetection: report.detectionDate,
    surveillanceType: toEnumValue(
      CreateReportDtoSurveillanceType,
      report.surveillanceType,
    ),
    description: report.description,
    numberOfDeaths: report.numberOfDeaths,
    numberOfSick: report.numberOfSicks,
    populationSize: report.populationSize,
    humanAffected: mapYesNoUnknownFromApi(report.humanAffected),
    domesticAnimalAffected: mapYesNoUnknownFromApi(
      report.domesticAnimalAffected,
    ),
    knowTheCause: knownCause,
    etiologyType: report.etiologyType?.code,
    etiologyAgent: report.etiologicalAgent?.code,
    diagnosticProcedure: hasDiagnosticProcedures ? 'yes' : 'no',
    ...(diagnosticProcedures && { diagnosticProcedures }),
    ...(supportingDocuments?.length && { supportingDocuments }),
  };
};
